import { useEffect } from 'react'
import Styles from './PromptBar.module.scss'
import { autoUpdate, shift, size, useFloating } from '@floating-ui/react-dom'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { MAIN_CONTENT_ID } from 'screens/ProseEdit/Constants'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { EditorView } from 'prosemirror-view'
import { isEmptyDoc } from 'common/Prose/utils/isEmptyDoc'
import { GenericPromptBar } from './GenericPromptBar'
import { EmptyPromptBar } from './EmptyPromptBar'
import { ArticleView } from 'types'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { isEmptyDocWithTitle } from 'common/Prose/utils/isEmptyDocWithTitle'

interface Props {
  articleId: string
  layerId: string
  view: EditorView
  anchor?: HTMLElement
  hidePromptBar?: boolean
}

export const FloatingPromptBar = observer((props: Props) => {
  const docStore = useRootStore().docStore
  const { hidePromptBar, anchor } = props
  const { isMobile } = useResponsiveDesign()

  const { x, y, reference, floating, middlewareData } = useFloating({
    placement: 'top',
    strategy: 'fixed',
    middleware: [
      shift({
        padding: isMobile ? 52 : 18,
        mainAxis: false,
        crossAxis: true,
      }),
      size({
        apply({ elements, rects }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  useEffect(() => {
    if (anchor) {
      reference(anchor)
    }
  }, [anchor, reference])

  if (hidePromptBar || !docStore?.permissions?.can_edit_content || docStore?.articleView !== ArticleView.Editing)
    return null

  const isVisible = !middlewareData.hide?.referenceHidden && x !== null && y !== null

  return (
    <FloatingPortal root={anchor?.closest(`#${MAIN_CONTENT_ID}`)}>
      <div
        className={Styles.container}
        ref={floating}
        style={{ top: y || 0, left: x || 0, visibility: isVisible ? 'visible' : 'hidden' }}
      >
        <PromptBar {...props} />
      </div>
    </FloatingPortal>
  )
})

export const PromptBar = observer(({ articleId, layerId, view, hidePromptBar }: Props) => {
  const isDocEmpty = isEmptyDoc(view.state) || isEmptyDocWithTitle(view.state)
  const docStore = useRootStore().docStore

  if (hidePromptBar || !docStore?.permissions?.can_edit_content || docStore?.articleView !== ArticleView.Editing)
    return null

  return (
    <>
      {isDocEmpty ? (
        <EmptyPromptBar articleId={articleId} layerId={layerId} view={view} />
      ) : (
        <GenericPromptBar articleId={articleId} layerId={layerId} view={view} />
      )}
    </>
  )
})

export const PromptBarContainer = observer((props: Props) => {
  const { isMobile } = useResponsiveDesign()

  if (isMobile) {
    return null
  }

  return <FloatingPromptBar {...props} />
})
